import {AnimationController} from "../3DController/AnimationController";
import {RenderEngine} from "../3DController/RenderEngine";
import {XRControls} from "./XRControls";

import {
  Scene,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  Object3D,
  Box3
} from "three";

import { EventEmitter } from "events";
import { ModelLoadController } from "../3DController/ModelLoad/ModelLoadController";
import { getXRShadowMaterial } from "./XRShadowMaterial";
import { AssetInfoTypes, Configuration, Configurator, IScene, ISceneInfo, VisibilityGroup, VisibilitySet } from "threngine";
import { HotspotController } from "../3DController/HotspotController";
import { DeviceOrientationCameraController } from "../3DController/DeviceOrientationCameraController";
import { createGlassReplacementMaterial } from "../3DController/CaptureController";
import { ConfiguratorModel } from "../3DController/ConfiguratorModel";
import { AudioSource } from "../3DController/AudioController";
import { EventController } from "../3DController/Event/EventController";
// import {IXRService} from "threngine";

export enum XRStatus {
  Scan,
  Place,
  Running,
  Stopped
}

export enum XRInitialScale {
  RealWorld,
  Table
}

enum XRPlacementMarkerState {
  Hidden,
  Scan,
  Place
}

const XR_ORIGINAL_GLASS_MATERIAL = "__xrOriginalGlassMaterial";

class XRPlacementMarker {
  private configurator:Configurator;
  private _scene:Scene;
  private _set:VisibilitySet;
  private _groupScan:VisibilityGroup;
  private _groupPlace:VisibilityGroup;
  private _state:XRPlacementMarkerState = XRPlacementMarkerState.Hidden;

  public get scene():Scene { return this._scene; }
  public get state():XRPlacementMarkerState { return this._state; }

  public constructor(engine:RenderEngine) {
    const config = engine.assetProvider.getAssets().find(
      a => a.infoType == AssetInfoTypes.Scene && a.info.name === "3D Marker"
    );

    /* Note: do NOT initialize the configurator.
     * This will mess up the state of our main scene.
     * TODO: discuss with Lorenz how we can support this type of usage in a clearer way.
     */
    this.configurator = new Configurator((<IScene>config).info.data as Configuration, engine.engine);
    this._scene = (<any>this.configurator)._scene;

    this._set = this.configurator.config.visibility_sets.find(vs => vs.name == "3DMarkers");
    this._groupScan  = this.configurator.config.visibility_groups.find(vg => vg.name == "3DMarker Initialize");
    this._groupPlace = this.configurator.config.visibility_groups.find(vg => vg.name == "3DMarker Placement");

    // We do a little hackery
    for (const child of this._scene.children) {
      child.rotation.y = Math.PI; // 180° rotation
    }
  }

  public setState(state:XRPlacementMarkerState):void {
    this._state = state;

    switch (state) {
      case XRPlacementMarkerState.Hidden: {
        this.configurator.setVisibilitySet(this._set.id, undefined);
        break;
      }
      case XRPlacementMarkerState.Scan: {
        this.configurator.setVisibilitySet(this._set.id, this._groupScan.id);
        break;
      }
      case XRPlacementMarkerState.Place: {
        this.configurator.setVisibilitySet(this._set.id, this._groupPlace.id);
        break;
      }
    }
  }
}

export class XREngine {
  public static EVENT_INTERIOR_EXTERIOR:string = "interiorExterior";
  public static EVENT_STATUS_CHANGE:string = "statusChange";

  public get active():boolean { return this._status != XRStatus.Stopped; }
  public get interior():boolean { return this._interior; }
  public get event():EventEmitter { return this._event; }

  public initialScale:XRInitialScale = XRInitialScale.RealWorld;

  private _status:XRStatus = XRStatus.Stopped;
  private _interior:boolean = false;
  private _event:EventEmitter = new EventEmitter();
  // private xr:IXRService;
  private xr:any;
  private scene:Scene;
  private placementComplete:boolean;
  private skydomeMeshes:Object3D[];
  private animationToken:any;
  private canvas:HTMLElement;
  private ui:HTMLElement;
  private interiorBB:Box3 = new Box3();
  private engine:RenderEngine;
  private animationController:AnimationController;
  private hotspotController:HotspotController;
  private deviceOrientationCameraController:DeviceOrientationCameraController;
  private modelController:ConfiguratorModel;
  private eventController:EventController;
  private configuratorScene:Scene;
  private controls:XRControls;
  private marker:XRPlacementMarker;
  // private didDisableDeviceOrientationControls:boolean;
  private environmentAudioSource:AudioSource = null;
  private environmentAudioMuteToken:any|null = null;
  private originalEnvironmentID:string;

  private shadowPlane:Mesh;
  private shadowOriginalMaterial:MeshBasicMaterial;

  public initialize(
    engine:RenderEngine,
    animationController:AnimationController,
    hotspotController:HotspotController,
    deviceOrientationCameraController:DeviceOrientationCameraController,
    modelController:ConfiguratorModel,
    eventController:EventController
  ):void {
    this.ui = document.getElementById("app-include-content");
    this.canvas = document.getElementById("scene-canvas");

    this.engine = engine;
    this.animationController = animationController;
    this.hotspotController = hotspotController;
    this.deviceOrientationCameraController = deviceOrientationCameraController;
    this.modelController = modelController;
    this.eventController = eventController;

    ModelLoadController.event.on(ModelLoadController.STATUS_LOADED, this.onSceneChanged);
  }

  public async enterAR():Promise<void> {
    if (this.deviceOrientationCameraController.enabled) {
      this.deviceOrientationCameraController.toggle();
    }

    this.scene = new Scene();
    this.scene.name = "XRScene";
    this.configuratorScene = this.engine.scene;
    this.xr = this.engine.engine.services.xr;
    this.engine.engine.services.render.domContainer.mount.style.display = "none";

    this.placementComplete = false;
    if (!this.marker) {
      this.marker = new XRPlacementMarker(this.engine);
    }
    this.marker.setState(XRPlacementMarkerState.Scan);
    this.scene.add(this.xr.camera); // ugh
    this.xr.camera.add(this.marker.scene);
    this.marker.scene.position.set(0, -1.5, -7.5);
    this.marker.scene.scale.set(1.5, 1.5, 1.5);

    this.initConfiguratorScene();

    const promise = this.xr.requestAR({
      scene: this.scene,
      domOverlay: {
        root: this.ui,
        required: true
      },
      requireHitTest: true,
      estimateLight: false
    });
    promise.catch(this.onSessionEnd);
    await promise;

    this.environmentAudioSource = null;
    this.environmentAudioMuteToken = null;

    this.event.emit(XREngine.EVENT_STATUS_CHANGE, XRStatus.Scan);

    this.xr.onFrame.subscribe(this.onFrame);
    this.xr.onExit.subscribe(this.onSessionEnd);
    this.canvas.addEventListener('click', this.onSelect);

    this.controls = new XRControls(this.configuratorScene, this.xr.camera);
    this.controls.install();

    this.setStatus(XRStatus.Scan);

    this.eventController.onXRBegin();

    // must happen after setStatus() so that light dimming is disabled properly.
    this.changeEnvironment();
  }

  public leaveAR():void {
    if (this.active) {
      this.xr.exit();
    }
  }

  private onFrame = async ():Promise<void> => {
    if (!this.placementComplete) {
      if (this.xr.defaultHitTest) {
        const result = this.xr.defaultHitTest.results[0];
        
        if (result) {
          if (this.marker.state != XRPlacementMarkerState.Place) {
            this.marker.setState(XRPlacementMarkerState.Place);
            this.scene.add(this.marker.scene);
          }

          this.marker.scene.position.set(
            result.position.x,
            result.position.y,
            result.position.z
          );

          this.marker.scene.quaternion.set(
            result.orientation.x,
            result.orientation.y,
            result.orientation.z,
            result.orientation.w
          );

          this.marker.scene.scale.set(0.5, 0.5, 0.5);

          this.setStatus(XRStatus.Place);
        }
      }
    }

    if (this.marker.state == XRPlacementMarkerState.Scan) {
      // Give the impression that the maker is on the ground...
      this.marker.scene.rotation.x = -this.xr.camera.rotation.x;
    }

    if (this.placementComplete) {
      this.updateInteriorExteriorState();
      this.replaceGlassMaterials();
    }

    this.hotspotController.hotspotScene.position.copy(this.configuratorScene.position);
    this.hotspotController.hotspotScene.rotation.copy(this.configuratorScene.rotation);
    this.hotspotController.hotspotScene.scale.copy(this.configuratorScene.scale);

    this.muteEnvironmentAudio();
  };

  private onSelect = async (event):Promise<void> => {
    if (this.marker.state != XRPlacementMarkerState.Place) {
      return;
    }

    this.scene.add(this.configuratorScene);
    this.marker.setState(XRPlacementMarkerState.Hidden);

    this.configuratorScene.position.copy(this.marker.scene.position);
    this.configuratorScene.rotation.copy(this.marker.scene.rotation);
    switch (this.initialScale) {
      case XRInitialScale.RealWorld:
        this.configuratorScene.scale.setScalar(1);
        break;
      case XRInitialScale.Table:
        this.configuratorScene.scale.setScalar(0.1);
        break;
    }

    this.placementComplete = true;
    this.setStatus(XRStatus.Running);
  };

  private onSessionEnd = async ():Promise<void> => {
    /* This should happen before setStatus(), so that anything
     * that listens on the 'AR session end' event can safely decide
     * to change the environment and/or skydome (i.e. Xray)*
     */
    await this.restoreEnvironment();

    this.setStatus(XRStatus.Stopped);

    this.unmuteEnvironmentAudio();

    // if (this.didDisableDeviceOrientationControls) {
    //   this.deviceOrientationCameraController.deviceOrientationControls.enabled = true;
    // }

    if (this.controls) {
      this.controls.remove();
    }

    // TODO: solve this via a visibility set or something.
    for (const mesh of this.skydomeMeshes) {
      mesh.visible = true;
    }

    this.engine.engine.services.render.domContainer.mount.style.display = "inherit";
    
    this.xr.onFrame.unsubsribe(this.onFrame);
    this.xr.onExit.unsubsribe(this.onSessionEnd);
    this.canvas.removeEventListener('click', this.onSelect);

    // Restore original scene transform
    this.configuratorScene.position.set(0, 0, 0);
    this.configuratorScene.rotation.set(0, 0, 0);
    this.configuratorScene.scale.set(1, 1, 1);
    this.hotspotController.hotspotScene.position.set(0, 0, 0);
    this.hotspotController.hotspotScene.rotation.set(0, 0, 0);
    this.hotspotController.hotspotScene.scale.setScalar(1);
    this.restoreShadowMaterial();
    this.restoreGlassMaterials();

    this.animationController.releaseToken(this.animationToken);

    this.eventController.onXRFinish();
  };

  private onSceneChanged = () => {
    if (!this.active) {
      return;
    }

    const newScene = this.engine.scene;

    if (this.configuratorScene) {
      newScene.position.copy(this.configuratorScene.position);
      newScene.quaternion.copy(this.configuratorScene.quaternion);
      newScene.scale.copy(this.configuratorScene.scale);
      this.scene.remove(this.configuratorScene);
    }

    this.configuratorScene = newScene;
    this.controls.scene = newScene;
    this.initConfiguratorScene();
    this.scene.add(newScene);

    // After we loaded a new scene, we need to inform the composer to still use the AR scene and camera.
    // TODO: solve this properly when there's time for it (aka. never).
    const composer = this.engine.engine.services.render.renderController.composer;
    composer.setScene(this.scene);
    composer.setCamera(this.xr.camera);
  };

  private setStatus(status:XRStatus) {
    if (status != this._status) {
      this._status = status;
      this.event.emit(XREngine.EVENT_STATUS_CHANGE, status);
    }
  }

  private initConfiguratorScene():void {
    // TODO: ideally make TAA anti-flicker work in WebXR instead of disabling.
    if (this.animationToken) {
      this.animationController.releaseToken(this.animationToken);
    }
    this.animationToken = this.animationController.acquireToken();

    // TODO: use a visibility set to achieve this or something.
    this.skydomeMeshes = [];
    this.configuratorScene.traverse(c => {
      if (c.name.includes("Skydome") && c.visible) {
        this.skydomeMeshes.push(c);
        c.visible = false;
      }
    });

    this.replaceShadowMaterial();
  }

  private replaceShadowMaterial():void {
    this.shadowPlane = <Mesh>this.configuratorScene.getObjectByName("Shadow#Ground_Shadow");
    if (this.shadowPlane) {
      this.shadowOriginalMaterial = <MeshBasicMaterial>this.shadowPlane.material;
      if (this.shadowOriginalMaterial) {
        this.shadowPlane.material = getXRShadowMaterial(this.shadowOriginalMaterial.map);
      }
    }
  }

  private restoreShadowMaterial():void {
    if (this.shadowPlane) {
      this.shadowPlane.material = this.shadowOriginalMaterial;
    }
  }

  private replaceGlassMaterials():void {
    this.configuratorScene.traverse(c => {
      // We exclude lights because they may use colored glass,
      // which the replacement material does not handle.
      // But at the same time we don't actually need to replace this glass.
      if ((c instanceof Mesh) &&
          Array.isArray(c.material) &&
          (c.material[1] instanceof MeshPhysicalMaterial) &&
         !c.name.includes("Light") &&
         !c.name.includes("light")
      ) {
        c.userData[XR_ORIGINAL_GLASS_MATERIAL] = c.material;
        c.material = createGlassReplacementMaterial(c.material);
      }
    })
  }

  private restoreGlassMaterials():void {
    this.configuratorScene.traverse(c => {
      // We need to check if the replacement material is still 
      // on the mesh, before we restore the original material.
      // This is because for example the X-Ray feature will replace materials too,
      // and if we return from XR during X-Ray mode we do not want to restore the original materials yet.
      // TODO: right now we use Array.isArray to check this. Improve this heuristic?
      if ((c instanceof Mesh) &&
          Array.isArray(c.material) &&
          c.userData.hasOwnProperty(XR_ORIGINAL_GLASS_MATERIAL)
      ) {
        c.material = c.userData[XR_ORIGINAL_GLASS_MATERIAL];
      }
    });
  }

  private updateInteriorExteriorState():void {
    // Get current interior node from which the bounding box is calculated.
    const interiorNode = this.scene.getObjectByName("INTERIOR");
    const interiorOld = this._interior;

    if (interiorNode) {
      // TODO: let the XR service expose its camera properly.
      const camera = (<any>this.xr).camera;

      this.interiorBB.setFromObject(interiorNode);
      this._interior = this.interiorBB.containsPoint(camera.position);

      if (interiorOld != this.interior) {
        this.event.emit(XREngine.EVENT_INTERIOR_EXTERIOR);
      }
    }
  }

  private muteEnvironmentAudio():void {
    const environmentAudioSource = this.modelController.environmentAudioSource;

    if (environmentAudioSource != this.environmentAudioSource) {
      if (this.environmentAudioSource && this.environmentAudioMuteToken) {
        this.environmentAudioSource.releaseMuteToken(this.environmentAudioMuteToken);
      }

      if (environmentAudioSource) {
        this.environmentAudioMuteToken = environmentAudioSource.acquireMuteToken();
      } else {
        this.environmentAudioMuteToken = null;
      }

      this.environmentAudioSource = environmentAudioSource;
    }
  }

  private unmuteEnvironmentAudio():void {
    if (this.modelController.environmentAudioSource && this.environmentAudioMuteToken) {
      this.modelController.environmentAudioSource.releaseMuteToken(this.environmentAudioMuteToken);
      this.environmentAudioMuteToken = null;
    }
  }

  private changeEnvironment():void {
    const environmentGroup = this.modelController.environmentGroup;

    this.originalEnvironmentID = environmentGroup.selections[environmentGroup.currentSelection].id;

    this.modelController.changeSelection(environmentGroup.groupID, "Light Studio");
  }

  private restoreEnvironment():Promise<void> {
    return this.modelController.changeSelection(this.modelController.environmentGroup.groupID, this.originalEnvironmentID);
  }
}